/**
 * Watchlist page
 */
import { useState, useMemo } from 'react'
import {
  useWatchlist,
  useTags,
  useAddToWatchlist,
  useUpdateWatchlistItem,
  useRemoveFromWatchlist,
  useSetWatchlistTags,
  useBulkUpdateWatchlistTags,
  useBulkDeleteWatchlist,
  useSortableTable,
} from '../hooks'
import { Button, Input, Modal, LoadingCard, SortableHeader } from '../components/common'
import { BulkActionBar } from '../components/BulkActionBar'
import type { WatchlistItem, WatchlistItemCreate, WatchlistItemUpdate, Tag } from '../types'
import type { BulkTagAction } from '../api/holdings'

function formatCurrency(value: number | null | undefined): string {
  if (value === null || value === undefined) return '-'
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value)
}

export function WatchlistPage() {
  // Filter state
  const [selectedTagFilter, setSelectedTagFilter] = useState<string>('')

  // Selection state (for bulk operations)
  const [selectedSymbols, setSelectedSymbols] = useState<Set<string>>(new Set())

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<WatchlistItem | null>(null)
  const [error, setError] = useState('')

  // Form state
  const [formData, setFormData] = useState<WatchlistItemCreate>({
    symbol: '',
    target_price: undefined,
    target_type: 'buy',
    thesis: undefined,
  })
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  // Queries
  const { data: watchlist, isLoading: watchlistLoading } = useWatchlist({
    tagIds: selectedTagFilter ? [selectedTagFilter] : undefined,
  })
  const { data: tags, isLoading: tagsLoading } = useTags()

  // Sortable table
  const { sortedData: sortedWatchlist, requestSort, getSortDirection } = useSortableTable(
    watchlist,
    { key: 'symbol', direction: 'asc' }
  )

  // Mutations
  const addItem = useAddToWatchlist()
  const updateItem = useUpdateWatchlistItem()
  const removeItem = useRemoveFromWatchlist()
  const setWatchlistTags = useSetWatchlistTags()
  const bulkUpdateTags = useBulkUpdateWatchlistTags()
  const bulkDeleteWatchlist = useBulkDeleteWatchlist()

  const isLoading = watchlistLoading || tagsLoading

  // Create tag lookup map
  const tagMap = useMemo(() => {
    return new Map((tags || []).map(t => [t.tag_id, t]))
  }, [tags])

  const openCreateModal = () => {
    setEditingItem(null)
    setFormData({
      symbol: '',
      target_price: undefined,
      target_type: 'buy',
      thesis: undefined,
    })
    setSelectedTags([])
    setError('')
    setIsModalOpen(true)
  }

  const openEditModal = (item: WatchlistItem) => {
    setEditingItem(item)
    setFormData({
      symbol: item.symbol,
      target_price: item.target_price,
      target_type: item.target_type,
      thesis: item.thesis,
    })
    setSelectedTags(item.tags || [])
    setError('')
    setIsModalOpen(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    try {
      if (editingItem) {
        const update: WatchlistItemUpdate = {
          target_price: formData.target_price,
          target_type: formData.target_type,
          thesis: formData.thesis,
        }
        await updateItem.mutateAsync({
          symbol: editingItem.symbol,
          data: update,
        })
        // Update tags if they changed
        const tagsChanged =
          JSON.stringify([...selectedTags].sort()) !==
          JSON.stringify([...(editingItem.tags || [])].sort())
        if (tagsChanged) {
          await setWatchlistTags.mutateAsync({
            symbol: editingItem.symbol,
            tagIds: selectedTags,
          })
        }
      } else {
        await addItem.mutateAsync(formData)
        // Set tags on the new item
        if (selectedTags.length > 0) {
          await setWatchlistTags.mutateAsync({
            symbol: formData.symbol.toUpperCase(),
            tagIds: selectedTags,
          })
        }
      }
      setIsModalOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed')
    }
  }

  const handleRemove = async (symbol: string) => {
    if (!confirm(`Remove ${symbol} from watchlist?`)) return

    try {
      await removeItem.mutateAsync(symbol)
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove item')
    }
  }

  const toggleTag = (tagId: string) => {
    setSelectedTags(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    )
  }

  // Selection handlers
  const toggleSelection = (symbol: string) => {
    setSelectedSymbols(prev => {
      const next = new Set(prev)
      if (next.has(symbol)) {
        next.delete(symbol)
      } else {
        next.add(symbol)
      }
      return next
    })
  }

  const toggleSelectAll = () => {
    if (!watchlist) return
    if (selectedSymbols.size === watchlist.length) {
      setSelectedSymbols(new Set())
    } else {
      setSelectedSymbols(new Set(watchlist.map(w => w.symbol)))
    }
  }

  const clearSelection = () => setSelectedSymbols(new Set())

  // Bulk action handlers
  const handleBulkTags = async (action: BulkTagAction, tagIds: string[]) => {
    await bulkUpdateTags.mutateAsync({
      symbols: Array.from(selectedSymbols),
      action,
      tagIds,
    })
  }

  const handleBulkDelete = async () => {
    await bulkDeleteWatchlist.mutateAsync(Array.from(selectedSymbols))
  }

  const isBulkLoading = bulkUpdateTags.isPending || bulkDeleteWatchlist.isPending

  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Watchlist</h1>
        <div className="flex items-center space-x-4">
          {/* Tag filter */}
          <select
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
            value={selectedTagFilter}
            onChange={(e) => setSelectedTagFilter(e.target.value)}
          >
            <option value="">All Tags</option>
            {tags?.map((tag) => (
              <option key={tag.tag_id} value={tag.tag_id}>
                {tag.name}
              </option>
            ))}
          </select>

          <Button onClick={openCreateModal}>Add Symbol</Button>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 w-10">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  checked={sortedWatchlist.length > 0 && selectedSymbols.size === sortedWatchlist.length}
                  onChange={toggleSelectAll}
                />
              </th>
              <SortableHeader
                label="Symbol"
                direction={getSortDirection('symbol')}
                onSort={() => requestSort('symbol')}
              />
              <SortableHeader
                label="Current Price"
                direction={getSortDirection('current_price')}
                onSort={() => requestSort('current_price')}
                align="right"
              />
              <SortableHeader
                label="Target"
                direction={getSortDirection('target_price')}
                onSort={() => requestSort('target_price')}
                align="right"
              />
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">
                Type
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Thesis
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {sortedWatchlist.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                  Your watchlist is empty. Add symbols you're interested in.
                </td>
              </tr>
            ) : (
              sortedWatchlist.map((item) => {
                const priceColor = item.target_hit ? 'text-green-600 font-medium' : 'text-gray-900'
                const targetLabel = item.target_type === 'buy' ? 'Buy' : 'Sell'
                const hitMessage = item.target_type === 'buy' ? 'Buy target!' : 'Sell target!'
                const itemTags = item.tags?.map(tagId => tagMap.get(tagId)).filter(Boolean) as Tag[]
                const isSelected = selectedSymbols.has(item.symbol)

                return (
                  <tr
                    key={item.symbol}
                    className={`hover:bg-gray-50 ${item.target_hit ? 'bg-green-50' : ''} ${isSelected ? 'bg-primary-50' : ''}`}
                  >
                    <td className="px-4 py-4 w-10">
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                        checked={isSelected}
                        onChange={() => toggleSelection(item.symbol)}
                      />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-gray-900">{item.symbol}</span>
                      {itemTags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {itemTags.map(tag => (
                            <span
                              key={tag.tag_id}
                              className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium"
                              style={{
                                backgroundColor: `${tag.color || '#6B7280'}20`,
                                color: tag.color || '#6B7280',
                              }}
                            >
                              {tag.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-right ${priceColor}`}>
                      {formatCurrency(item.current_price)}
                      {item.target_hit && (
                        <span className="ml-2 text-xs text-green-600">{hitMessage}</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                      {formatCurrency(item.target_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-center">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        item.target_type === 'buy'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-orange-100 text-orange-800'
                      }`}>
                        {targetLabel}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500 max-w-md">
                      <p className="truncate">{item.thesis || '-'}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-right space-x-2">
                      <button
                        className="text-primary-600 hover:text-primary-900"
                        onClick={() => openEditModal(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="text-red-600 hover:text-red-900"
                        onClick={() => handleRemove(item.symbol)}
                      >
                        Remove
                      </button>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Watchlist Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingItem ? `Edit ${editingItem.symbol}` : 'Add to Watchlist'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {!editingItem && (
            <Input
              label="Symbol"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
              placeholder="e.g., NVDA"
              required
            />
          )}

          <Input
            label="Target Price (optional)"
            type="number"
            step="0.01"
            min="0"
            value={formData.target_price || ''}
            onChange={(e) => setFormData({
              ...formData,
              target_price: parseFloat(e.target.value) || undefined
            })}
            helperText="You'll be highlighted when price reaches this level"
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Target Type
            </label>
            <div className="flex gap-4">
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="target_type"
                  value="buy"
                  checked={formData.target_type === 'buy'}
                  onChange={() => setFormData({ ...formData, target_type: 'buy' })}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2 text-sm text-gray-700">Buy (alert when below)</span>
              </label>
              <label className="inline-flex items-center">
                <input
                  type="radio"
                  name="target_type"
                  value="sell"
                  checked={formData.target_type === 'sell'}
                  onChange={() => setFormData({ ...formData, target_type: 'sell' })}
                  className="form-radio h-4 w-4 text-primary-600"
                />
                <span className="ml-2 text-sm text-gray-700">Sell (alert when above)</span>
              </label>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Investment Thesis (optional)
            </label>
            <textarea
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              rows={3}
              value={formData.thesis || ''}
              onChange={(e) => setFormData({ ...formData, thesis: e.target.value || undefined })}
              placeholder="Why are you watching this stock?"
            />
          </div>

          {/* Tags multi-select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags
            </label>
            {tags?.length === 0 ? (
              <p className="text-sm text-gray-500">
                No tags created yet.{' '}
                <a href="/tags" className="text-primary-600 hover:underline">Create some tags</a>
              </p>
            ) : (
              <div className="flex flex-wrap gap-2">
                {tags?.map((tag) => {
                  const isSelected = selectedTags.includes(tag.tag_id)
                  return (
                    <button
                      key={tag.tag_id}
                      type="button"
                      onClick={() => toggleTag(tag.tag_id)}
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-1'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{
                        backgroundColor: `${tag.color || '#6B7280'}20`,
                        color: tag.color || '#6B7280',
                        borderColor: tag.color || '#6B7280',
                        ...(isSelected ? { ringColor: tag.color || '#6B7280' } : {}),
                      }}
                    >
                      {tag.name}
                      {isSelected && (
                        <svg className="ml-1 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  )
                })}
              </div>
            )}
          </div>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="secondary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              isLoading={addItem.isPending || updateItem.isPending || setWatchlistTags.isPending}
            >
              {editingItem ? 'Save Changes' : 'Add to Watchlist'}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Bulk action bar */}
      <BulkActionBar
        selectedCount={selectedSymbols.size}
        onClearSelection={clearSelection}
        tags={tags}
        onBulkTags={handleBulkTags}
        onBulkDelete={handleBulkDelete}
        isLoading={isBulkLoading}
      />
    </div>
  )
}
